import React, { useEffect, useState } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ArticleDashboard = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingArticleId, setEditingArticleId] = useState(null);
  const [editedArticle, setEditedArticle] = useState({});
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedArticleData, setSelectedArticleData] = useState(null);
  const [viewingArticle, setViewingArticle] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const getTodayDate = () => { return new Date().toISOString().split('T')[0];};
  const [categoryInput, setCategoryInput] = useState("");
  const [editCategoryInput, setEditCategoryInput] = useState("");
  
  const [currentPage, setCurrentPage] = useState(1);
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const itemsPerPage = 5;

  const [newArticle, setNewArticle] = useState({
    articleTitle: "",
    articleAuthor: "",
    articleDescription: "",
    articleContent: [{ contentHeader: "", contentDescription: "" }],
    articleImage: "",
    articleCategory: [],
    articleSDG: [],
    articleFeatured: false,
    articleActive: true,
    articleDate: getTodayDate()
  });

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    const token = localStorage.getItem("token");
    if (!token) {setTimeout(() => {toast.error("No token found. Please log in.");}, 0); return;
    }
    setLoading(true);
    try {
      const response = await fetch("https://tbido-website-api.azurewebsites.net/getArticles", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setArticles(data.articles || []);
        if (!toast.isActive("admin-articles-loaded")) {toast.success("Articles loaded successfully", {toastId: "admin-articles-loaded"});}
      } else {
        toast.error(data.message || "Failed to fetch articles");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching articles");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (articleId, articleData) => {
    setEditingArticleId(articleId);

    let formattedDate = getTodayDate();
  
    if (articleData.articleDate) {
      if (articleData.articleDate.$date) {
        formattedDate = new Date(articleData.articleDate.$date).toISOString().split('T')[0];
      } 
      else {
        const dateObj = new Date(articleData.articleDate);
        if (!isNaN(dateObj.getTime())) {
          formattedDate = dateObj.toISOString().split('T')[0];
        }
      }
    }

    setEditedArticle({ 
      ...articleData, 
      articleDate: formattedDate,
      articleCategory: articleData.articleCategory,
      originalArticleTitle: articleData.articleTitle
    });
  };

  const handleInputChange = (field, value) => {
    const booleanField = field === "articleActive" || field === "articleFeatured";
    const parsedValue = booleanField ? value === "true" : value;
    setEditedArticle((prev) => ({ ...prev, [field]: parsedValue }));
  };

  const handleSave = async () => {
    const token = localStorage.getItem("token");
    if (!token) return toast.error("No token found. Please log in.");
    const payload = {
      _id: editedArticle._id,
      updatedArticleTitle: editedArticle.articleTitle,
      updatedArticleAuthor: editedArticle.articleAuthor,
      updatedArticleFeatured: editedArticle.articleFeatured,
      articleActive: editedArticle.articleActive,
    };
    try {
      const response = await fetch("https://tbido-website-api.azurewebsites.net/editArticleDetails", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (response.ok) {
        setArticles((prev) =>
          prev.map((e) => (e._id === editingArticleId ? { ...e, ...editedArticle } : e))
        );
        toast.success("Article updated successfully");
        setEditingArticleId(null);
      } else {
        toast.error(data.error || "Failed to update article");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating article");
    }
  };

  const handleCancel = () => {
    setEditingArticleId(null);
    setEditedArticle({});
  };

  const fetchArticle = async (articleId, articleTitle) => {
    const token = localStorage.getItem("token");
    if (!token) return toast.error("No token found. Please log in.");
    setLoading(true);
    try {
      const response = await fetch(`https://tbido-website-api.azurewebsites.net/getArticleById/${encodeURIComponent(articleId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setSelectedArticleData(data.article);
        setSelectedArticle(articleId);
        setViewingArticle(true);
        toast.success(`Loaded ${articleTitle} successfully`);
      } else {
        toast.error(data.message || `Failed to fetch article`);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching article");
    } finally {
      setLoading(false);
    }
  };

  const handleDataChange = (e) => {
    const { name, value } = e.target;
    setSelectedArticleData({
      ...selectedArticleData,
      [name]: value
    });
  };

  const handleContentChange = (index, e) => {
    const { name, value } = e.target;
    const updatedContents = [...selectedArticleData.articleContent];
    updatedContents[index] = {...updatedContents[index],[name]: value};
    setSelectedArticleData({ ...selectedArticleData, articleContent: updatedContents });
  };

  const addContent = () => {
      setSelectedArticleData({
        ...selectedArticleData,
        articleContent: [...selectedArticleData.articleContent, { contentHeader: '', contentDescription: '' }],
      });
    };

    const removeContent = (index) => {
      if (selectedArticleData.articleContent.length > 1) {
        const updatedContent = [...selectedArticleData.articleContent];
        updatedContent.splice(index, 1);
        setSelectedArticleData({ ...selectedArticleData, articleContent: updatedContent });
      } else {
        toast.error('At least 1 content are required.');
      }
    };

    const validateForm = () => {
        const errors = {};
        if (selectedArticleData.articleDescription.length == 0) errors.name = 'Description must not be empty.';
        if (selectedArticleData.articleContent.length == 0) errors.email = 'Content must not be empty.';
        if (selectedArticleData.articleImage.trim().length === 0) errors.idea = 'There must be an image for the article.';
        return errors;
      };
    
      const handleFinalizeArticle= async (articleId, e) => {
        e.preventDefault();
        const errors = validateForm();
        if (Object.keys(errors).length) {
          toast.error(Object.values(errors).join('\n'));
          return;
        }
    
        setLoading(true);
        try {
          const res = await fetch(`https://tbido-website-api.azurewebsites.net/postArticle/${encodeURIComponent(articleId)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              articleDescription: selectedArticleData.articleDescription,
              articleContent: selectedArticleData.articleContent,
              articleImage: selectedArticleData.articleImage,
              articleCategory: selectedArticleData.articleCategory,
              articleDate: new Date(selectedArticleData.articleDate),
              articleSDG: selectedArticleData.articleSDG,
            }),
          });
    
          if (res.ok) {
            toast.success('Update successful!');
          } else {
            toast.error('Error.');
          }
        } catch (err) {
          console.error(err);
          toast.error('Error. Please try again.');
        } finally {
          setLoading(false);
        }
      };

      const handleNewArticleChange = (e) => {
        const { name, value } = e.target;
        setNewArticle({ ...newArticle, [name]: value });
      };

      const submitNewArticle = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
          const response = await fetch("https://tbido-website-api.azurewebsites.net/addArticle", { // Ensure this route exists
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newArticle),
          });
          if (response.ok) {
            toast.success("Article created successfully!");
            setIsAddModalOpen(false);
            fetchArticles(); // Refresh the list
          } else {
            toast.error("Failed to create article.");
          }
        } catch (error) {
          toast.error("Error connecting to server.");
        } finally {
          setLoading(false);
        }
      };

      const addNewContent = () => {
        setNewArticle({
          ...newArticle,
          articleContent: [...newArticle.articleContent, { contentHeader: '', contentDescription: '' }],
        });
      };

      const handleNewContentChange = (index, e) => {
        const { name, value } = e.target;
        const updatedContents = [...newArticle.articleContent];
        updatedContents[index] = { ...updatedContents[index], [name]: value };
        setNewArticle({ ...newArticle, articleContent: updatedContents });
      };

      const removeNewContent = (index) => {
        if (newArticle.articleContent.length > 1) {
          const updatedContent = [...newArticle.articleContent];
          updatedContent.splice(index, 1);
          setNewArticle({ ...newArticle, articleContent: updatedContent });
        }
      };

      const toggleSDG = (sdgNumber) => {
        setNewArticle(prev => {
          const isSelected = prev.articleSDG.includes(sdgNumber);
          const updatedSDGs = isSelected
            ? prev.articleSDG.filter(id => id !== sdgNumber) // Remove if already selected
            : [...prev.articleSDG, sdgNumber].sort((a, b) => a - b); // Add and sort numerically
          
          return { ...prev, articleSDG: updatedSDGs };
        });
      };

      const toggleEditSDG = (sdgNumber) => {
        setSelectedArticleData(prev => {
          const currentSDGs = prev.articleSDG || []; // Ensure it's an array
          const isSelected = currentSDGs.includes(sdgNumber);
          const updatedSDGs = isSelected
            ? currentSDGs.filter(id => id !== sdgNumber)
            : [...currentSDGs, sdgNumber].sort((a, b) => a - b);
          
          return { ...prev, articleSDG: updatedSDGs };
        });
      };

      const handleAddCategory = (e, isEditing = false) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const value = isEditing ? editCategoryInput.trim() : categoryInput.trim();
          
          if (value) {
            const currentCats = isEditing ? selectedArticleData.articleCategory : newArticle.articleCategory;
            if (!currentCats.includes(value)) {
              if (isEditing) {
                setSelectedArticleData({ ...selectedArticleData, articleCategory: [...currentCats, value] });
                setEditCategoryInput("");
              } else {
                setNewArticle({ ...newArticle, articleCategory: [...currentCats, value] });
                setCategoryInput("");
              }
            }
          }
        }
      };

      const removeCategory = (indexToRemove, isEditing = false) => {
        if (isEditing) {
          setSelectedArticleData({
            ...selectedArticleData,
            articleCategory: selectedArticleData.articleCategory.filter((_, index) => index !== indexToRemove)
          });
        } else {
          setNewArticle({
            ...newArticle,
            articleCategory: newArticle.articleCategory.filter((_, index) => index !== indexToRemove)
          });
        }
      };

  const closeArticleView = () => {
    setViewingArticle(false);
    setSelectedArticle(null);
    setSelectedArticleData(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    toast.success('Logged out successfully');
    setTimeout(() => {
      window.close(); // Close the tab
    }, 1000);
  };

  // Data processing for Filtering and Pagination
  const getParsedDate = (articleDate) => {
    if (!articleDate) return null;
    if (articleDate.$date) return new Date(articleDate.$date);
    const dateObj = new Date(articleDate);
    return isNaN(dateObj.getTime()) ? null : dateObj;
  };

  const availableYears = [...new Set(articles.map(a => {
      const date = getParsedDate(a.articleDate);
      return date ? date.getFullYear() : null;
  }).filter(y => y !== null))].sort((a, b) => b - a);

  const filteredArticles = articles.filter(article => {
      const date = getParsedDate(article.articleDate);
      if (!date) return true; // keep articles without valid dates in list 

      const articleMonth = date.getMonth() + 1; 
      const articleYear = date.getFullYear();

      const matchMonth = filterMonth ? articleMonth === parseInt(filterMonth) : true;
      const matchYear = filterYear ? articleYear === parseInt(filterYear) : true;

      return matchMonth && matchYear;
  });

  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage) || 1;
  const paginatedArticles = filteredArticles.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
  );

  const handleMonthChange = (e) => {
      setFilterMonth(e.target.value);
      setCurrentPage(1); // Reset to page 1 on filter
  };

  const handleYearChange = (e) => {
      setFilterYear(e.target.value);
      setCurrentPage(1); // Reset to page 1 on filter
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      {viewingArticle ? (
        // Article View
        <>
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0 pr-4">
                  <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate" title={selectedArticleData?.articleTitle}> 
                    {selectedArticleData?.articleTitle}
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">
                    Written by: {selectedArticleData.articleAuthor}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={closeArticleView}
                    className="px-6 py-2.5 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-all duration-200 shadow-md"
                  >
                    ← Back to Articles
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-blue-800 text-white rounded-lg font-medium hover:from-red-700 hover:to-blue-900 transition-all duration-200 shadow-md"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
                  <div className="w-16 h-16 border-4 border-red-600 rounded-full absolute top-0 left-0 animate-spin border-t-transparent"></div>
                </div>
                <p className="mt-4 text-gray-600 font-medium">Loading article...</p>
              </div>
            ) : selectedArticleData ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* bg-gradient-to-r from-red-600 to-blue-800 text-white */}
                <div className="overflow-x-auto"> 
                  <form
                    onSubmit={(e) => e.preventDefault()}
                    className="w-full p-6 overflow-y-auto"
                  > 
                    {loading && <Loader />}
                    {/* Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                      <div className="flex flex-col">
                        <label className="block text-sm font-bold text-gray-700 mb-2 text-left">Image Preview</label>
                        <div className="w-full h-full min-h-[250px] bg-gray-100 rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 flex items-center justify-center">
                          {selectedArticleData.articleImage ? (
                            <img 
                              src={selectedArticleData.articleImage} 
                              alt="Preview" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-gray-400">No Image URL provided</span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col space-y-4 text-left">
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-2">Article Image URL</label>
                          <input
                            type="text"
                            placeholder="https://example.com/image.jpg"
                            value={selectedArticleData.articleImage}
                            onChange={(e) => setSelectedArticleData({...selectedArticleData, articleImage: e.target.value})}
                            className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                          />
                        </div>
                        <div className="flex-grow">
                          <label className="block text-sm font-bold text-gray-700 mb-2">Short Intro / Description</label>
                          <textarea
                            placeholder="Enter a brief summary..."
                            value={selectedArticleData.articleDescription}
                            onChange={(e) => setSelectedArticleData({...selectedArticleData, articleDescription: e.target.value})}
                            className="w-full h-[calc(100%-32px)] p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contents */}
                    <label className="block text-sm font-bold text-gray-700 mb-6 text-left">Article Content Blocks</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {selectedArticleData.articleContent.map((content, index) => (
                        <div key={index} className="relative bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col">
                          
                          {/* Delete Button */}
                          {selectedArticleData.articleContent.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeContent(index)}
                              className="absolute -top-3 -right-3 bg-red-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs shadow-lg hover:bg-red-700 transition-colors z-10"
                            >
                              ✕
                            </button>
                          )}

                          <div className="flex flex-col h-full space-y-4 text-left">
                            <div>
                              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                                Section Header {index + 1}
                              </label>
                              <input
                                type="text"
                                name="contentHeader"
                                placeholder="Enter Section Title..."
                                value={content.contentHeader}
                                onChange={(e) => handleContentChange(index, e)}
                                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-800"
                              />
                            </div>

                            <div className="flex-grow pb-12">
                              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                                Paragraph Description
                              </label>
                              <ReactQuill
                                theme="snow"
                                value={content.contentDescription}
                                onChange={(value) => {
                                  const updatedContents = [...selectedArticleData.articleContent];
                                  updatedContents[index] = { ...updatedContents[index], contentDescription: value };
                                  setSelectedArticleData({ ...selectedArticleData, articleContent: updatedContents });
                                }}
                                className="bg-white h-48"
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <button 
                        type="button" 
                        onClick={addContent} 
                        className="flex flex-col items-center justify-center min-h-[250px] border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50/30 transition-all group"
                      >
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2 group-hover:bg-blue-100">
                          <span className="text-2xl font-light">+</span>
                        </div>
                        <span className="font-bold text-sm uppercase tracking-wider">Add New Block</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                      <div className="space-y-4 text-left">
                        <label className="block text-sm font-bold text-gray-700">Update Categories</label>
                        <input
                          type="text"
                          placeholder="Press Enter to add..."
                          value={editCategoryInput}
                          onChange={(e) => setEditCategoryInput(e.target.value)}
                          onKeyDown={(e) => handleAddCategory(e, true)}
                          className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                        />
                        {/* Dashed Category Box */}
                        <div className="w-full min-h-[130px] p-3 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex flex-wrap gap-2 content-start">
                          {selectedArticleData.articleCategory?.map((cat, index) => (
                            <div key={index} className="flex items-center gap-2 bg-gradient-to-r from-red-800 to-blue-900 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-md">
                              {cat}
                              <button type="button" onClick={() => removeCategory(index, true)} className="hover:text-red-300 ml-1">×</button>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-4 text-left">
                        <label className="block text-sm font-bold text-gray-700">Update Date</label>
                        <input 
                          type="date" 
                          value={selectedArticleData.articleDate} 
                          onChange={(e) => setSelectedArticleData({ ...selectedArticleData, articleDate: e.target.value })}
                          className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                        />
                        {/* Dashed Date Preview Box */}
                        <div className="w-full min-h-[130px] p-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center text-center">
                          <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Published On</h4>
                          <p className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-800 to-blue-900">
                            {new Date(selectedArticleData.articleDate).toLocaleDateString('en-US', { 
                              month: 'long', day: 'numeric', year: 'numeric' 
                            })}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 font-medium">
                            {new Date(selectedArticleData.articleDate).toLocaleDateString('en-US', { weekday: 'long' })}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-gray-700 font-bold mb-2">Article SDGs:</label>

                      {/* Container 1: SELECTED SDGs (Current) */}
                      <div className="min-h-[60px] p-3 border-2 border-dashed border-blue-200 rounded-xl bg-blue-50 mb-4 flex flex-wrap gap-2">
                        {(selectedArticleData.articleSDG || []).length === 0 && (
                          <p className="text-blue-400 text-sm italic">No SDGs selected. Click below to add.</p>
                        )}
                        {(selectedArticleData.articleSDG || []).map((num) => (
                          <div 
                            key={num} 
                            onClick={() => toggleEditSDG(num)}
                            className="cursor-pointer hover:scale-105 transition-transform relative group"
                          >
                            <img 
                              src={`/images/SDGs/SDG${num}.jpg`} 
                              alt={`SDG ${num}`} 
                              className="h-12 w-12 rounded shadow-md border-2 border-white"
                            />
                            <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100">
                              ✕
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Container 2: AVAILABLE SDGs */}
                      <div className="grid grid-cols-6 sm:grid-cols-9 gap-2 p-2 bg-gray-50 rounded-lg">
                        {Array.from({ length: 17 }, (_, i) => i + 1).map((num) => {
                          const isSelected = (selectedArticleData.articleSDG || []).includes(num);
                          return (
                            <button
                              key={num}
                              type="button"
                              onClick={() => toggleEditSDG(num)}
                              disabled={isSelected}
                              className={`relative transition-all ${isSelected ? 'opacity-20 grayscale cursor-default' : 'hover:scale-110'}`}
                            >
                              <img 
                                src={`/images/SDGs/SDG${num}.jpg`} 
                                alt={`SDG ${num}`} 
                                className="rounded shadow-sm"
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <br/>
                    {/* Action Buttons */}
                    <div className="flex flex-wrap justify-end gap-4">
                      <button onClick={closeArticleView} className="rounded-full px-5 py-1.5 border border-gray-400 text-gray-700 hover:bg-gray-200">Cancel</button>
                      
                      <button onClick={(e) => handleFinalizeArticle(selectedArticleData._id, e)} className="rounded-full bg-[linear-gradient(45deg,#800000,#000080)] text-white rounded px-4 py-2">
                        Update
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <p className="text-gray-600">No articles found.</p>
              </div>
            )}
          </div>
        </>
      ) : (
        // Article Dashboard View
        <>
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Article <span className="font-normal">Dashboard</span>
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  View the current status and update articles
                </p>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate('/admin-nav')}
                  className="px-4 sm:px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-all duration-200 shadow-sm"
                >
                  ← Back to Nav
                </button>

                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="px-4 sm:px-6 py-2.5 bg-gradient-to-r from-red-600 to-blue-800 text-white rounded-lg font-medium hover:from-red-700 hover:to-blue-900 transition-all duration-200 shadow-md"
                >
                  + Add Article
                </button>
                
                <button
                  onClick={handleLogout}
                  className="px-4 sm:px-6 py-2.5 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-all duration-200 shadow-md"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>

          {/* Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
                  <div className="w-16 h-16 border-4 border-red-600 rounded-full absolute top-0 left-0 animate-spin border-t-transparent"></div>
                </div>
                <p className="mt-4 text-gray-600 font-medium">Loading articles...</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                
                {/* Filter Bar */}
                <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-4 items-center">
                  <h3 className="text-sm font-semibold text-gray-700 mr-2">Filter Articles:</h3>
                  <select
                    value={filterMonth}
                    onChange={handleMonthChange}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">All</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={m}>
                        {new Date(0, m - 1).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                  <select
                    value={filterYear}
                    onChange={handleYearChange}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">All</option>
                    {availableYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <div className="flex-1 text-right text-sm text-gray-500">
                    Showing {paginatedArticles.length} of {filteredArticles.length} articles
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-red-600 to-blue-800 text-white">
                        <th className="px-6 py-4 text-left text-sm font-semibold">Title</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold">Date</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold">Author</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold">Featured</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold">Archived</th>
                        <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedArticles.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                            <div className="flex flex-col items-center justify-center">
                              <span className="text-3xl mb-2">📅</span>
                              <p className="text-base font-medium text-gray-900">No articles found</p>
                              <p className="text-sm mt-1">There are no articles published for the selected month and year.</p>
                            </div>
                          </td>
                        </tr>
                      ) : paginatedArticles.map((article) => {
                          const parsedDateObj = getParsedDate(article.articleDate);
                          const formattedDateStr = parsedDateObj 
                            ? parsedDateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) 
                            : 'N/A';

                        return (
                          <tr key={article._id} className="hover:bg-gray-50 transition-colors">
                            {editingArticleId === article._id ? (
                              <>
                                <td className="px-6 py-4">
                                  <input 
                                    value={editedArticle.articleTitle}
                                    onChange={(e) => handleInputChange("articleTitle", e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                  />
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 italic">
                                  {formattedDateStr}
                                </td>
                                <td className="px-6 py-4">
                                  <input 
                                    value={editedArticle.articleAuthor}
                                    onChange={(e) => handleInputChange("articleAuthor", e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                  />
                                </td>
                                <td className="px-6 py-4">
                                  <select 
                                    value={editedArticle.articleFeatured}
                                    onChange={(e) => handleInputChange("articleFeatured", e.target.value)}
                                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                  >
                                    <option value={true}>Yes</option>
                                    <option value={false}>No</option>
                                  </select>
                                </td>
                                <td className="px-6 py-4 text-center">
                                  <select 
                                    value={editedArticle.articleActive}
                                    onChange={(e) => handleInputChange("articleActive", e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                  >
                                    <option value={false}>Yes</option>
                                    <option value={true}>No</option>
                                  </select>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex justify-center gap-2">
                                    <button 
                                      onClick={handleSave}
                                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                                    >
                                      Save
                                    </button>
                                    <button 
                                      onClick={handleCancel}
                                      className="px-4 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-6 py-4 text-sm font-medium text-gray-900 max-w-md">
                                  {article.articleTitle}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                                  {formattedDateStr}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700">
                                  <div className="line-clamp-2">{article.articleAuthor}</div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700 text-center">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                    article.articleFeatured
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {article.articleFeatured ? 'Yes' : 'No'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700 text-center">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                    !article.articleActive 
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {article.articleActive ? 'No' : 'Yes'}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex justify-center gap-2">
                                    <button 
                                      onClick={() => handleEdit(article._id, article)}
                                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 transition-colors"
                                    >
                                      Change Details
                                    </button>
                                    <button 
                                      onClick={() => fetchArticle(article._id, article.articleTitle)}
                                      className="px-4 py-2 bg-gradient-to-r from-red-600 to-blue-800 text-white rounded-lg text-sm font-medium hover:from-red-700 hover:to-blue-900 transition-colors"
                                    >
                                      Edit Content
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page <span className="font-semibold text-gray-900">{currentPage}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {isAddModalOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <h2 className="text-xl font-bold text-gray-800">Create New Article</h2>
            <button onClick={() => setIsAddModalOpen(false)} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
          </div>
          
          <form onSubmit={submitNewArticle} className="p-6 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="flex flex-col text-left">
              <label className="block text-sm font-bold text-gray-700 mb-2">Article Image Preview</label>
              <div className="w-full h-full min-h-[350px] bg-gray-100 rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 flex items-center justify-center relative">
                {newArticle.articleImage ? (
                  <img 
                    src={newArticle.articleImage} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/600x400?text=Invalid+Image+URL'; }}
                  />
                ) : (
                  <div className="text-center p-6">
                    <div className="text-4xl mb-2 text-gray-300">🖼️</div>
                    <span className="text-gray-400 font-medium">No image URL provided yet</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col space-y-4 text-left">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Article Title</label>
                <input 
                  type="text" 
                  name="articleTitle" 
                  required 
                  placeholder="Enter the main headline..."
                  value={newArticle.articleTitle}
                  onChange={handleNewArticleChange} 
                  className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Author</label>
                  <input 
                    type="text" 
                    name="articleAuthor" 
                    required 
                    placeholder="John Doe"
                    value={newArticle.articleAuthor}
                    onChange={handleNewArticleChange} 
                    className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Image URL</label>
                  <input 
                    type="text" 
                    name="articleImage" 
                    placeholder="https://..."
                    value={newArticle.articleImage}
                    onChange={handleNewArticleChange} 
                    className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" 
                  />
                </div>
              </div>

              <div className="flex-grow">
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Short Intro / Summary</label>
                <textarea 
                  name="articleDescription" 
                  rows="5" 
                  placeholder="Write a brief hook for the article..."
                  value={newArticle.articleDescription}
                  onChange={handleNewArticleChange} 
                  className="w-full h-[calc(100%-30px)] p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                />
              </div>
            </div>
          </div>

          <div className="pt-4">
            <label className="block text-sm font-bold text-gray-700 mb-4 text-left uppercase tracking-widest text-gray-400">Article Content Blocks</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {newArticle.articleContent.map((content, index) => (
                <div key={index} className="relative bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col group">
                  
                  {newArticle.articleContent.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeNewContent(index)}
                      className="absolute -top-3 -right-3 bg-red-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-xs shadow-lg hover:bg-red-700 transition-colors z-10"
                    >
                      ✕
                    </button>
                  )}

                  <div className="flex flex-col h-full space-y-4 text-left">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Section Header {index + 1}</label>
                      <input
                        type="text"
                        name="contentHeader"
                        placeholder="Enter Section Title..."
                        value={content.contentHeader}
                        onChange={(e) => handleNewContentChange(index, e)}
                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-800"
                      />
                    </div>

                    <div className="flex-grow pb-12">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                        Paragraph Content
                      </label>
                      <ReactQuill
                        theme="snow"
                        value={content.contentDescription}
                        onChange={(value) => {
                          const updatedContents = [...newArticle.articleContent];
                          updatedContents[index] = { ...updatedContents[index], contentDescription: value };
                          setNewArticle({ ...newArticle, articleContent: updatedContents });
                        }}
                        className="bg-white h-48"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button 
                type="button" 
                onClick={addNewContent} 
                className="flex flex-col items-center justify-center min-h-[280px] border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50/30 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors">
                  <span className="text-2xl font-light text-gray-500 group-hover:text-blue-600">+</span>
                </div>
                <span className="font-bold text-sm uppercase tracking-wider">Add New Block</span>
              </button>
            </div>
          </div>
            
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            <div className="space-y-4 text-left">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Add Category</label>
                <div className="relative group">
                  <input
                    type="text"
                    placeholder="Type and press Enter..."
                    value={categoryInput}
                    onChange={(e) => setCategoryInput(e.target.value)}
                    onKeyDown={(e) => handleAddCategory(e, false)}
                    className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none shadow-sm transition-all"
                  />
                  <div className="absolute right-3 top-3.5">
                    <kbd className="px-2 py-0.5 text-[10px] font-bold text-gray-400 bg-gray-50 border border-gray-200 rounded-md">ENTER</kbd>
                  </div>
                </div>
              </div>

              {/* Dashed Container for Categories */}
              <div className="w-full min-h-[130px] p-3 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex flex-wrap gap-2 content-start overflow-y-auto">
                {newArticle.articleCategory.length === 0 ? (
                  <div className="flex items-center justify-center w-full h-full text-gray-400 text-xs italic">
                    No categories added yet
                  </div>
                ) : (
                  newArticle.articleCategory.map((cat, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-2 bg-gradient-to-r from-red-800 to-blue-900 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm"
                    >
                      {cat}
                      <button type="button" onClick={() => removeCategory(index, false)} className="hover:text-red-300 ml-1 text-lg leading-none">×</button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right Column: Date Input & Styled Preview */}
            <div className="flex flex-col text-left">
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Article Publication Date</label>
              <div className="relative mb-4">
                <input 
                  type="date" 
                  value={newArticle.articleDate} 
                  onChange={(e) => setNewArticle({ ...newArticle, articleDate: e.target.value })}
                  className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none shadow-sm transition-all cursor-pointer font-medium text-gray-700"
                />
              </div>

              {/* Dashed Container for Date Preview (Matching Left Side) */}
              <div className="w-full min-h-[130px] p-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center text-center">
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Date Preview</h4>
                <p className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-800 to-blue-900">
                  {new Date(newArticle.articleDate).toLocaleDateString('en-US', { 
                    month: 'long', day: 'numeric', year: 'numeric' 
                  })}
                </p>
                <p className="text-xs text-gray-500 mt-1 font-medium">
                  {new Date(newArticle.articleDate).toLocaleDateString('en-US', { weekday: 'long' })}
                </p>
              </div>
            </div>
          </div>

            <div className="mb-6">
              <label className="block text-gray-700 font-bold mb-2">Select SDGs:</label>

              {/* Container 1: SELECTED SDGs */}
              <div className="min-h-[60px] p-3 border-2 border-dashed border-blue-200 rounded-xl bg-blue-50 mb-4 flex flex-wrap gap-2">
                {newArticle.articleSDG.length === 0 && (
                  <p className="text-blue-400 text-sm italic">No SDGs selected yet. Click below to add.</p>
                )}
                {newArticle.articleSDG.map((num) => (
                  <div 
                    key={num} 
                    onClick={() => toggleSDG(num)}
                    className="cursor-pointer hover:scale-105 transition-transform relative group"
                  >
                    <img 
                      src={`/images/SDGs/SDG${num}.jpg`} 
                      alt={`SDG ${num}`} 
                      className="h-12 w-12 rounded shadow-md border-2 border-white"
                    />
                    <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100">
                      ✕
                    </div>
                  </div>
                ))}
              </div>

              {/* Container 2: AVAILABLE SDGs */}
              <div className="grid grid-cols-6 sm:grid-cols-9 gap-2 p-2 bg-gray-50 rounded-lg">
                {Array.from({ length: 17 }, (_, i) => i + 1).map((num) => {
                  const isSelected = newArticle.articleSDG.includes(num);
                  return (
                    <button
                      key={num}
                      type="button"
                      onClick={() => toggleSDG(num)}
                      disabled={isSelected}
                      className={`relative transition-all ${isSelected ? 'opacity-20 grayscale cursor-default' : 'hover:scale-110'}`}
                    >
                      <img 
                        src={`/images/SDGs/SDG${num}.jpg`} 
                        alt={`SDG ${num}`} 
                        className="rounded shadow-sm"
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 text-gray-600 font-medium">Cancel</button>
              <button type="submit" onClick={submitNewArticle} className="px-6 py-2.5 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-all duration-200 shadow-md">
                Create Article
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </div>
  );
};

export default ArticleDashboard;